import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { Autocomplete, Box, Button, Grid2, TextField } from "@mui/material";
import {
	TestShackModeChips,
	TestShackRangeControls,
} from "../TestShackPeriodFilters";
import type {
	TestShackReportEventOption,
	TestShackReportFilterOptions,
	TestShackReportHeatOption,
	TestShackReportSectionOption,
} from "@/pages/products/test-shack/models/TestShackReport";
import type {
	TestShackReportUrlFilters,
	TestShackReportUrlFilterUpdate,
} from "@/pages/products/test-shack/hooks/useTestShackReportUrlState";

interface TestShackReportFiltersProps {
	assetId: number;
	filters: TestShackReportUrlFilters;
	options: TestShackReportFilterOptions;
	isLoadingOptions: boolean;
	onChange: (updates: TestShackReportUrlFilterUpdate) => void;
}

const sectionLabel = (section: TestShackReportSectionOption) =>
	section.SectionName;

const eventLabel = (event: TestShackReportEventOption) => {
	const start = dxtToLocalServerTime(event.Start, "MM/dd/yy HH:mm");
	return `#${event.EventId}${start ? ` · ${start}` : ""}`;
};

const heatLabel = (heat: TestShackReportHeatOption) => heat.HeatName;

export const TestShackReportFilters = ({
	assetId,
	filters,
	options,
	isLoadingOptions,
	onChange,
}: TestShackReportFiltersProps) => {
	const isShiftMode = filters.mode === "shift";
	const selectedSection =
		options.sections.find(
			(section) => section.SectionId === filters.sectionId,
		) ?? null;
	const selectedEvent =
		options.events.find((event) => event.EventId === filters.eventId) ?? null;
	const selectedHeat =
		options.heats.find((heat) => heat.HeatId === filters.heatId) ?? null;

	const reset = () =>
		onChange({
			...(isShiftMode ? {} : { datePreset: "last7days" as const }),
			sectionId: null,
			eventId: null,
			heatId: null,
			problemsOnly: false,
			showMinTargetMax: false,
		});

	return (
		<>
			<TestShackModeChips
				mode={filters.mode}
				datePreset={filters.datePreset}
				onChange={onChange}
			/>

			<Grid2 container spacing={1} alignItems="center">
				<TestShackRangeControls
					assetId={assetId}
					mode={filters.mode}
					startDate={filters.startDate}
					endDate={filters.endDate}
					datePreset={filters.datePreset}
					onChange={onChange}
				/>

				<Grid2 size={{ xs: 12, md: 1.5 }}>
					<Autocomplete
						options={options.sections}
						value={selectedSection}
						loading={isLoadingOptions}
						getOptionLabel={sectionLabel}
						isOptionEqualToValue={(option, value) =>
							option.SectionId === value.SectionId
						}
						onChange={(_event, option) =>
							onChange({
								sectionId: option?.SectionId ?? null,
								eventId: null,
								heatId: null,
							})
						}
						noOptionsText="No sections in this date range"
						renderInput={(params) => (
							<TextField
								{...params}
								label="Section"
								size="small"
								sx={{ backgroundColor: "background.paper" }}
							/>
						)}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 2.1 }}>
					<Autocomplete
						options={options.events}
						value={selectedEvent}
						loading={isLoadingOptions}
						disabled={filters.sectionId === null}
						getOptionLabel={eventLabel}
						isOptionEqualToValue={(option, value) =>
							option.EventId === value.EventId
						}
						onChange={(_event, option) =>
							onChange({ eventId: option?.EventId ?? null, heatId: null })
						}
						noOptionsText="No events for this section"
						renderInput={(params) => (
							<TextField
								{...params}
								label="Event"
								size="small"
								sx={{ backgroundColor: "background.paper" }}
							/>
						)}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 1.5 }}>
					<Autocomplete
						options={options.heats}
						value={selectedHeat}
						loading={isLoadingOptions}
						getOptionLabel={heatLabel}
						isOptionEqualToValue={(option, value) =>
							option.HeatId === value.HeatId
						}
						onChange={(_event, option) =>
							onChange({ heatId: option?.HeatId ?? null })
						}
						noOptionsText="No recorded heats for these filters"
						renderInput={(params) => (
							<TextField
								{...params}
								label="Heat"
								size="small"
								sx={{ backgroundColor: "background.paper" }}
							/>
						)}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: "grow" }}>
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<Button
							startIcon={<RestartAltOutlinedIcon />}
							onClick={reset}
							sx={{ textTransform: "none" }}
						>
							Reset filters
						</Button>
					</Box>
				</Grid2>
			</Grid2>
		</>
	);
};
